import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { AssetsService } from '@sc/data-access-assets';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Dossier,
  DossierLastStepForm,
  Fitter,
  InsuranceSuggestion,
  LocationData,
  LocationDataGeocoded,
  NotificationType,
  contactPeriodTimeValues,
  emailPattern,
  HTMLElementEvent,
} from '@sc/data-access-models';
import {
  FittersSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { TranslatorService } from '@sc/data-access-translation';
import {
  FeatureFlippingManagerService,
  FeatureToggleDirective,
} from '@sc/feature-flipping';
import { FocusManagerService } from '@sc/feature-focus-manager';
import { DeclarationConditions } from '@sc/utils';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { LabelRequiredPipe } from '../../pipes';
import { ErrorMessagesService } from '../../services';
import { CustomValidators } from '../../validators';
import { AsteriskInfoComponent } from '../asterisk-info';
import { ButtonComponent } from '../button';
import { ErrorComponent } from '../error';
import { GpdrComponent } from '../gpdr';
import { InputTextComponent } from '../input-text';
import { MarketingDataCheckboxComponent } from '../marketing-data-checkbox';
import { PhoneInputComponent } from '../phone';
import { SelectComponent } from '../select';
import { SpinnerComponent } from '../spinner';
import { CheckboxComponent } from '../checkbox';

@Component({
  selector: 'sc-dossier-last-step-form',
  styleUrls: ['./dossier-last-step-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    ReactiveFormsModule,
    ErrorComponent,
    GpdrComponent,
    MarketingDataCheckboxComponent,
    InputTextComponent,
    AsteriskInfoComponent,
    PhoneInputComponent,
    SpinnerComponent,
    SelectComponent,
    ButtonComponent,
    LabelRequiredPipe,
    MatRadioModule,
    MatFormFieldModule,
    TranslateModule,
    AsyncPipe,
    FeatureToggleDirective,
    CheckboxComponent,
  ],
  template: ` <form [formGroup]="form" sc-focus-return-zone>
    <fieldset id="last-step-form-section-1">
      <div class="customer-infos-container">
        <legend
          class="first-section-legend"
          [ngClass]="{
            'sc-design-title-h2': !isCallBack,
            'sc-design-title-h1': isCallBack
          }"
        >
          {{ getFirstSectionLegendKey() | translate }}
        </legend>

        <sc-asterisk-info></sc-asterisk-info>
        <div class="name-container">
          <div class="first-name-container">
            <sc-input-text
              class="input"
              formControlName="firstname"
              autocomplete="given-name"
              [label]="
                'VerifyCustomerDataBottomSheet.firstnameLabel' | translate
              "
            ></sc-input-text>
          </div>

          <div class="last-name-container">
            <sc-input-text
              class="input"
              formControlName="lastname"
              autocomplete="family-name"
              [label]="
                'VerifyCustomerDataBottomSheet.lastnameLabel' | translate
              "
            ></sc-input-text>
          </div>
        </div>

        <div class="email-container" *ngIf="!isCallBack">
          <sc-input-text
            class="input"
            formControlName="email"
            autocomplete="email"
            errorPattern="emailPattern"
            [hint]="
              'GlobalApplicativeConstants.accessibility.labels.emailExemple'
                | translate
            "
            [label]="'VerifyCustomerDataBottomSheet.emailLabel' | translate"
            ariaDescribedby="email-additional-hint"
          ></sc-input-text>
        </div>

        <div
          id="email-additional-hint"
          class="normal-text sc-design-text-4"
          *ngIf="!isCallBack"
        >
          <p>{{ 'VerifyCustomerDataBottomSheet.emailText' | translate }}</p>
        </div>

        <div class="phone-container">
          <sc-phone-input
            class="input"
            formControlName="phone"
            [label]="'VerifyCustomerDataBottomSheet.phoneLabel' | translate"
            scKeyPress="phoneNumber"
            [displayCountrySelection]="true"
            [setCountry]="form?.get('phoneCountry')?.value"
            ariaDescribedby="phone-additional-hint"
            (changeCountry)="form?.get('phoneCountry').setValue($event)"
          ></sc-phone-input>
        </div>

        <div
          id="phone-additional-hint"
          class="normal-text sc-design-text-4"
          *ngIf="!isCallBack"
        >
          <p>{{ 'VerifyCustomerDataBottomSheet.phoneText' | translate }}</p>
        </div>

        <div class="customer-location-container" *ngIf="isCallBack">
          <div class="address-bloc" *ngIf="showUnknownAddressForm">
            <div>
              <sc-input-text
                id="city"
                type="text"
                autocomplete="address-level2"
                [label]="'VerifyCustomerDataBottomSheet.city' | translate"
                [value]="modifiedLocation?.addressElements?.city"
                [forceHighlightRequired]="false"
                (input)="onChangeCity($event)"
              ></sc-input-text>
            </div>
            <div>
              <sc-input-text
                id="postalCode"
                type="text"
                autocomplete="postal-code"
                [label]="'VerifyCustomerDataBottomSheet.postalCode' | translate"
                [value]="modifiedLocation?.addressElements?.postalCode"
                [forceHighlightRequired]="false"
                (input)="onChangePostalCode($event)"
              ></sc-input-text>
            </div>
          </div>

          <div>
            <div class="form-row" *ngIf="!showUnknownAddressForm">
              <div class="form-data">
                <sc-input-text
                  formControlName="customerLocation"
                  type="text"
                  autocomplete="address-level1"
                  [label]="
                    'VerifyCustomerDataBottomSheet.customerAddress' | translate
                  "
                  suggestionsLabelName="address"
                  [value]="selectedAddressStr"
                  [suggestions]="googleAutoCompleteResponse"
                  [forceHighlightRequired]="false"
                  (updateSuggestions)="onUpdateAddressSuggestions($event)"
                  (selectSuggestion)="onSelectAddress($event)"
                ></sc-input-text>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="fitter-selection-container"
        *ngIf="
          isCallBack &&
          (!!modifiedLocation || selectedAddressStr?.length) &&
          !showUnknownAddressForm
        "
      >
        <mat-label class="text-grey-dark">
          <label
            for="fitter-select"
            [innerHTML]="
              'VerifyCustomerDataBottomSheet.fitterSelection' | translate
            "
          ></label>
        </mat-label>

        <div class="fitter-spinner" *ngIf="!fitterSelectList?.length">
          <sc-spinner></sc-spinner>
        </div>
        <sc-select
          id="fitter-select"
          *ngIf="fitterSelectList?.length"
          formControlName="selectedFitter"
          [options]="fitterSelectList"
          [disabled]="fitterSelectList?.length === 1"
          (valueChange)="onFitterSelect($event)"
        >
          <ng-template #triggerTemplate let-item>
            <span>{{ item?.ihm }}</span>
          </ng-template>

          <ng-template #optionTemplate let-item>
            <span>{{ item?.ihm }} </span>
          </ng-template>
        </sc-select>
      </div>
      <div class="contact-period-container" *ngIf="isCallBack">
        <mat-label class="text-grey-dark">
          <label
            for="contactPeriod-select"
            [innerHTML]="
              'VerifyCustomerDataBottomSheet.contactPeriodLabel'
                | labelRequired: this.isCallBack
            "
          ></label>
        </mat-label>
        <sc-select
          id="contactPeriod-select"
          formControlName="contactPeriod"
          [options]="contactPeriodTimeValues"
          [attr.data-testid]="'contactPeriod'"
        >
        </sc-select>
      </div>
    </fieldset>

    <fieldset id="last-step-form-section-2">
      <legend
        class="question-title sc-design-title-h2"
        *ngIf="('LastStep.QuestionsTitle' | translate).length > 0"
      >
        {{ 'LastStep.QuestionsTitle' | translate }}
      </legend>

      <div
        class="question insurance-question-body"
        *ngIf="
          featureFlippingService.isFeatureActivated(
            'b2c-can-declare-insurance'
          ) && !isCallBack
        "
      >
        <div class="insurance-choice">
          <mat-label class="question-text sc-design-text-4">
            <label
              for="insuranceChoice-radio-group"
              [innerHTML]="
                'VerifyCustomerDataBottomSheet.insuranceNameQuestion'
                  | labelRequired
                    : featureFlippingService.isFeatureActivated(
                        'b2c-field-required-insurance-choice'
                      )
              "
            ></label>
          </mat-label>
        </div>
        <mat-radio-group
          id="insuranceChoice-radio-group"
          formControlName="insuranceChoice"
          (change)="onInsuranceChoiceChange($event)"
          [attr.data-testid]="'insuranceChoiceRadio'"
        >
          <mat-radio-button [value]="'true'"
            ><span class="sc-design-text-3">{{
              'GlobalApplicativeConstants.action.yes' | translate
            }}</span>
          </mat-radio-button>
          <mat-radio-button [value]="'false'"
            ><span class="sc-design-text-3">{{
              'GlobalApplicativeConstants.action.no' | translate
            }}</span>
          </mat-radio-button>
          <mat-radio-button [value]="'unknown'"
            ><span class="sc-design-text-3">{{
              'GlobalApplicativeConstants.action.unknown' | translate
            }}</span>
          </mat-radio-button>
        </mat-radio-group>
        <div
          class="container-input"
          *ngIf="form?.get('insuranceChoice')?.value === 'true'"
        >
          <div class="insurance-container">
            <div class="insurance-input-container">
              <sc-input-text
                class="input"
                formControlName="insuranceName"
                [suggestions]="filteredInsuranceSuggestion"
                (input)="onInputChange($event)"
                (selectSuggestion)="onSelectSuggestion($event)"
                (focusout)="onInputFocusOut()"
                (focusin)="onInputFocusIn()"
                [label]="
                  'VerifyCustomerDataBottomSheet.insuranceName' | translate
                "
                errorPattern="requiredCompanyName"
              >
              </sc-input-text>
            </div>

            <div
              class="insurance-checkbox-container"
              *ngIf="
                featureFlippingService.isFeatureActivated(
                  'b2c-insurance-custom-name'
                )
              "
            >
              <sc-checkbox
                id="insurance-checkbox"
                class="input"
                [value]="customInsuranceNameEnabled"
                [disabled]="isUniqueInsuranceSuggestion()"
                (changeValue)="onCustomInsuranceCheckboxChange($event)"
                (mousedown)="onCheckDown()"
                (mouseup)="onCheckUp()"
                [attr.data-testid]="'custom-insurance-checkbox'"
              >
                {{
                  'VerifyCustomerDataBottomSheet.insuranceRadioButton'
                    | translate
                }}
              </sc-checkbox>
            </div>
          </div>
        </div>
        <div
          class="container-img"
          *ngIf="form?.get('insuranceName')?.value && displayInsurerLogo()"
        >
          <img
            [src]="getLogoPath()"
            [alt]="
              (getLogoAlt(configurationService.getConfig()?.id, 'configIdLogo')
                | async) || configurationService.getConfig()?.insurerName
            "
          />
          <span class="is-approved-by">
            {{ 'VerifyCustomerDataBottomSheet.insuranceLogoLabel' | translate }}
          </span>

          <img
            *ngIf="displayInsurerLogo() && !errorImage"
            [src]="getInsurerLogoPath()"
            (error)="errorImage = true"
            [alt]="
              (getLogoAlt(form?.get('insuranceName')?.value, 'insurerIdLogo')
                | async) || form?.get('insuranceName')?.value
            "
          />
          <span
            *ngIf="displayInsurerLogo() && errorImage"
            class="is-approved-by image-not-found"
            >{{ form?.get('insuranceName')?.value }}</span
          >
        </div>
        <sc-error
          [displayCondition]="
            (form?.get('insuranceChoice')?.value === 'false' ||
              form?.get('insuranceChoice')?.value === 'unknown') &&
            featureFlippingService.isFeatureActivated(
              'b2c-display-no-insurance-warning'
            )
          "
          [error]="{
            title:
              'VerifyCustomerDataBottomSheet.noInsuranceWarningtTitle'
              | translate,
            message:
              'VerifyCustomerDataBottomSheet.noInsuranceWarningtText'
              | translate
          }"
        ></sc-error>
      </div>

      <div
        class="question"
        *ngIf="
          featureFlippingService.isFeatureActivated(
            'b2c-can-declare-car-ownership'
          ) && !isCallBack
        "
      >
        <div class="question-text sc-design-text-4">
          <mat-label>
            <label
              for="ownerShipType-radio-group"
              [innerHTML]="
                'VerifyCustomerDataBottomSheet.ownerShipQuestion'
                  | labelRequired
                    : featureFlippingService.isFeatureActivated(
                        'b2c-field-required-ownership'
                      )
              "
            ></label>
          </mat-label>
        </div>
        <mat-radio-group
          id="ownerShipType-radio-group"
          formControlName="ownerShipType"
          (change)="onOwnerShipTypeChange($event)"
          [attr.data-testid]="'ownerShipTypeRadio'"
        >
          <mat-radio-button
            *ngFor="let ownerShipType of ownerShipTypes"
            [value]="ownerShipType"
            [checked]="form?.get('ownerShipType')?.value === ownerShipType"
          >
            <span class="sc-design-text-3">{{
              'GlobalApplicativeConstants.ownerShipType.' + ownerShipType
                | translate
            }}</span></mat-radio-button
          >
        </mat-radio-group>
        <div *ngFor="let ownerShipType of ownerShipTypes">
          <div
            class="container-input"
            *ngIf="
              form?.get('ownerShipType')?.value === ownerShipType &&
              ('GlobalApplicativeConstants.ownerShipName.' + ownerShipType
                | translate)
            "
          >
            <sc-input-text
              class="input"
              formControlName="ownerShipName"
              [label]="
                'GlobalApplicativeConstants.ownerShipName.' + ownerShipType
                  | translate
              "
            ></sc-input-text>
          </div>
        </div>
      </div>

      <div class="question">
        <label
          class="question-text sc-design-text-4"
          [innerHTML]="
            getCommentQuestionKey() | labelRequired: isCommentRequired()
          "
          id="comment-for-fitter-label"
          for="comment-for-fitter"
        ></label>
        <p
          class="question-helper sc-design-text-4 "
          id="comment-for-fitter-helper"
        >
          {{ 'LastStep.textareaPlaceHolderHelper' | translate }}
        </p>
        <textarea
          aria-labelledby="comment-for-fitter-label"
          aria-describedby="comment-for-fitter-helper"
          id="comment-for-fitter"
          formControlName="message"
          [placeholder]="getCommentPlaceholderKey() | translate"
        ></textarea>
      </div>

      <div
        class="question"
        *ngIf="
          featureFlippingService.isFeatureActivated(
            'b2c-can-declare-notification-type'
          ) &&
          (preferredNotificationList.length > 1 ||
            !preferredNotificationList.includes('Email')) &&
          !isCallBack
        "
      >
        <div
          class="question-text sc-design-text-4"
          [innerHTML]="'LastStep.Question2Text' | labelRequired: false"
        ></div>
        <mat-radio-group formControlName="notificationType">
          <mat-radio-button
            *ngFor="let choice of preferredNotificationList"
            [value]="choice"
            [checked]="form?.get('notificationType')?.value === choice"
          >
            <span class="sc-design-text-3">{{
              'AppointmentValidation.notification.' + choice | translate
            }}</span>
          </mat-radio-button>
        </mat-radio-group>
      </div>
    </fieldset>
    <div class="form-row" *ngIf="displayCheckboxRow()">
      <div class="form-data">
        <sc-gpdr
          *scFeatureToggle="'b2c-field-required-gpdr'"
          formControlName="gpdr"
        ></sc-gpdr>
        <sc-marketing-data-checkbox
          *scFeatureToggle="'b2c-field-display-marketing-data'"
          formControlName="marketingData"
        ></sc-marketing-data-checkbox>
      </div>
    </div>

    <sc-button
      (click)="bookAppointment()"
      id="nextButton"
      [attr.data-testid]="'submitButton'"
      [disabled]="!canBook()"
    >
      {{ getValidationButtonKey() | translate }}
    </sc-button>
  </form>`,
})
export class DossierLastStepFormComponent implements OnInit, OnDestroy {
  @Input() currentDossier: Dossier;
  @Input() currentFitter: Fitter;
  @Input() isCallBack = false;
  @Output() bookClickEvent = new EventEmitter<DossierLastStepForm>();

  public form: FormGroup;
  preferredNotificationList: NotificationType[] = [];

  isCustomerEmailIncomplete: boolean;

  ownerShipTypes =
    this.configurationService.getConfig().carOwnerShipTypes
      .carOwnerShipTypesList;
  insuranceSuggestion: InsuranceSuggestion[] = [];
  filteredInsuranceSuggestion: string[] = [];

  contactPeriodTimeValues = contactPeriodTimeValues?.map((element) =>
    this.translator.getInstantTranslation(element)
  );
  fitterSelectList: {
    fitterId: string;
    ihm: string;
    brandId: string;
    timezone: string;
  }[];
  fittersByAddress: Fitter[];

  errorImage: boolean;

  modifiedLocation: LocationDataGeocoded;
  showUnknownAddressForm: boolean;
  selectedAddressStr: string;
  googleAutoCompleteResponse: LocationData[] = [];
  private readonly addressSuggestionsSubject$ = new Subject<string>();

  fetchFitterDistanceIcreased = false;
  customInsuranceNameEnabled = false;
  clickingCheckbox = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    public translator: TranslatorService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public configurationService: ConfigurationService,
    public featureFlippingService: FeatureFlippingManagerService,
    public errorMessagesService: ErrorMessagesService,
    public assetsService: AssetsService,
    private readonly sandboxService: SandboxService,
    private readonly focusManager: FocusManagerService,
    public fittersSB: FittersSandboxService,
    private readonly declarationConditions: DeclarationConditions
  ) {
    this.addressSuggestionsSubject$
      .pipe(
        debounceTime(500),
        filter((value) => value.length >= 3)
      )
      .subscribe(($event: string) => {
        this.sandboxService.getPlacesFromAddress($event).subscribe({
          next: (places) => {
            this.googleAutoCompleteResponse = places;
            this.googleAutoCompleteResponse.push({
              address: this.translator.getInstantTranslation(
                'VerifyCustomerDataBottomSheet.addressNotFound'
              ),
              isCompleteAddress: false,
              isKnownAddress: false,
              locationProvider: 'NONE',
              placeId: null,
            });
            this.changeDetectorRef.detectChanges();
          },
          error: () => {
            this.googleAutoCompleteResponse = [];
            this.googleAutoCompleteResponse.push({
              address: this.translator.getInstantTranslation(
                'VerifyCustomerDataBottomSheet.addressNotFound'
              ),
              isCompleteAddress: false,
              isKnownAddress: false,
              locationProvider: 'NONE',
              placeId: null,
            });
            this.changeDetectorRef.detectChanges();
          },
        });
      });
  }

  ngOnInit() {
    this.isCustomerEmailIncomplete =
      this.currentDossier?.customer?.email?.substring(
        this.currentDossier?.customer?.email?.length - 24
      ) === 'noreply@saint-gobain.com' || !this.currentDossier?.customer?.email;

    this.form = this.formBuilder.group({
      firstname: new FormControl(
        {
          value: this.currentDossier?.customer?.firstname ?? null,
          disabled: !!this.currentDossier?.customer?.firstname,
        },
        [Validators.required]
      ),
      lastname: new FormControl(
        {
          value: this.currentDossier?.customer?.lastname ?? null,
          disabled: !!this.currentDossier?.customer?.lastname,
        },
        [Validators.required]
      ),
      email: new FormControl(
        {
          value: this.isCustomerEmailIncomplete
            ? ''
            : this.currentDossier?.customer?.email,
          disabled:
            this.featureFlippingService.isFeatureActivated(
              'b2c-disable-email-modif'
            ) && !this.isCustomerEmailIncomplete,
        },
        this.isCallBack
          ? null
          : [Validators.required, Validators.pattern(emailPattern)]
      ),
      phone: new FormControl(
        this.currentDossier?.customer?.phoneNumber?.number,
        [Validators.required]
      ),
      phoneCountry: new FormControl(
        this.currentDossier?.customer?.phoneNumber?.countryCode
          ? this.currentDossier?.customer?.phoneNumber?.countryCode
          : this.configurationService.getDefaultPhoneNumberCountry()
      ),
      contactPeriod: [null, this.isCallBack ? Validators.required : null],
      message: ['', this.isCommentRequired() ? [Validators.required] : null],
      notificationType: [],
      ownerShipType: new FormControl(
        this.currentDossier?.claim?.car?.ownerShip?.type,
        this.featureFlippingService.isFeatureActivated(
          'b2c-field-required-ownership'
        ) && !this.isCallBack
          ? [Validators.required]
          : null
      ),
      ownerShipName: new FormControl(
        this.currentDossier?.claim?.car?.ownerShip?.name
      ),
      insuranceChoice: new FormControl(
        {
          value:
            this.isInsuranceNameEmpty() &&
            !this.isUniqueInsuranceSuggestion() &&
            !this.getForcedInsurance()
              ? ''
              : 'true',
          disabled: this.isUniqueInsuranceSuggestion(),
        },
        this.featureFlippingService.isFeatureActivated(
          'b2c-field-required-insurance-choice'
        ) && !this.isCallBack
          ? [Validators.required]
          : null
      ),
      insuranceName: new FormControl({
        value:
          this.isInsuranceNameEmpty() && !this.isUniqueInsuranceSuggestion() //if empty name AND not unique suggestion
            ? '' // then value is empty
            : this.isUniqueInsuranceSuggestion() // else, if unique suggestion
            ? this.getUniqueSuggestionName() //then value is this unique suggestion
            : this.currentDossier?.policyInformation?.insuranceName, // else value is insuranceName from current dossier
        disabled: this.isUniqueInsuranceSuggestion(),
      }),
      gpdr: [
        null,
        this.featureFlippingService.isFeatureActivated(
          'b2c-field-required-gpdr'
        )
          ? Validators.required
          : Validators.nullValidator,
      ],
      marketingData: [null],
    });

    if (this.isCallBack) {
      this.form.addControl(
        'customerLocation',
        this.formBuilder.control(this.modifiedLocation, Validators.required)
      );
      this.form.addControl(
        'selectedFitter',
        this.formBuilder.control(null, [])
      );

      if (this.currentDossier?.searchCriteria?.address) {
        this.modifiedLocation = this.currentDossier?.searchCriteria?.address;
        this.selectedAddressStr =
          this.currentDossier?.searchCriteria.address?.address;

        this.showUnknownAddressForm =
          !this.currentDossier?.searchCriteria?.address?.addressElements
            ?.city ||
          !this.currentDossier?.searchCriteria?.address?.addressElements
            ?.postalCode;

        this.fetchFittersFromGeoCode(
          this.currentDossier?.searchCriteria?.address?.geoCoordinates?.lat,
          this.currentDossier?.searchCriteria?.address?.geoCoordinates?.lon,
          15
        );
        this.form.get('customerLocation').patchValue(this.selectedAddressStr);
        this.changeDetectorRef.markForCheck();
      }
    }
    this.preferredNotificationList =
      this.configurationService.getConfig().preferredNotificationList;
    this.translator.getCurrentLang().subscribe(() => {
      this.contactPeriodTimeValues = contactPeriodTimeValues?.map((element) =>
        this.translator.getInstantTranslation(element)
      );
      this.changeDetectorRef.detectChanges();
    });

    if (
      this.featureFlippingService.isFeatureActivated(
        'insurance-suggestion-from-iclist'
      )
    ) {
      this.sandboxService.getICList().subscribe((liste) => {
        this.insuranceSuggestion = [...liste];
      });
    } else {
      this.insuranceSuggestion = [
        ...this.configurationService.getSortedInsuranceSuggestions(),
      ];
    }

    this.filteredInsuranceSuggestion = this.insuranceSuggestion?.map(
      (x) => x.insuranceName
    );
  }

  private getCustomerLocationValidators() {
    const resultValidators: ValidatorFn[] = [Validators.required];

    if (!this.showUnknownAddressForm) {
      resultValidators.push(
        CustomValidators.locationHasPlaceId(() => this.modifiedLocation)
      );
    }

    return resultValidators;
  }

  private getSelectedFitterValidators() {
    const resultValidators: ValidatorFn[] = [];

    if (!this.showUnknownAddressForm) {
      resultValidators.push(Validators.required);
    }

    return resultValidators;
  }

  private updateFormValidators() {
    this.form
      .get('customerLocation')
      .setValidators(this.getCustomerLocationValidators());
    this.form
      .get('selectedFitter')
      .setValidators(this.getSelectedFitterValidators());

    this.form.updateValueAndValidity();
    this.changeDetectorRef.markForCheck();
  }

  public isUniqueInsuranceSuggestion(): boolean {
    return (
      this.configurationService.getConfig().insuranceSuggestion.length === 1 ||
      !!this.getForcedInsurance()
    );
  }

  getForcedInsurance(): InsuranceSuggestion | null {
    const originFilter = this.configurationService.getDossierGlobalOrigin(
      this.currentDossier?.creationData
    );

    if (originFilter?.options?.forceInsuranceCode) {
      return (
        this.configurationService
          .getSortedInsuranceSuggestions()
          .find(
            (insurance) =>
              insurance.internalInsuranceCode ===
              originFilter.options.forceInsuranceCode
          ) || null
      );
    }

    return null;
  }

  canBook() {
    return (
      (this.form?.valid && !this.isCallBack) ||
      (this.form?.valid &&
        !!this.modifiedLocation?.addressElements?.city &&
        !!this.modifiedLocation?.addressElements?.postalCode)
    );
  }

  private isInsuranceNameEmpty(): boolean {
    return (
      !this.currentDossier?.policyInformation?.insuranceName ||
      this.currentDossier?.policyInformation?.insuranceName === 'NONE'
    );
  }

  private getUniqueSuggestionName(): string {
    return (
      this.getForcedInsurance()?.insuranceName ||
      this.configurationService?.getConfig()?.insuranceSuggestion[0]
        ?.insuranceName
    );
  }

  getFirstSectionLegendKey() {
    return this.isCallBack
      ? 'VerifyCustomerDataBottomSheet.mainTitle'
      : 'VerifyCustomerDataBottomSheet.questionTitle';
  }

  getValidationButtonKey() {
    return this.isCallBack
      ? 'LastStep.buttonCallBackDesktopConfirm'
      : 'LastStep.buttonDesktopConfirm';
  }

  getLogoPath() {
    return this.assetsService.getConfigInsurerLogoPath();
  }

  getInsurerLogoPath() {
    const insuranceChoiceId = this.insuranceSuggestion.find(
      (value) => value.insuranceName === this.form?.get('insuranceName')?.value
    )?.id;
    return this.assetsService.getInsurerAssetPath(insuranceChoiceId + '.png');
  }

  getLogoAlt(
    item: string,
    itemType: 'insurerIdLogo' | 'configIdLogo'
  ): Observable<string> {
    if (itemType === 'insurerIdLogo') {
      item = this.insuranceSuggestion.find(
        (value) => value.insuranceName === item
      )?.id;
    }
    return this.assetsService.getLogoAlt(item, itemType);
  }

  isCommentRequired() {
    return this.declarationConditions.isGlassMultiple(
      this.currentDossier?.claim?.glass
    );
  }

  getCommentPlaceholderKey() {
    if (
      this.declarationConditions.isGlassMultiple(
        this.currentDossier?.claim?.glass
      )
    ) {
      return 'LastStep.textareaPlaceHolderMultiple';
    }

    return 'LastStep.textareaPlaceHolder';
  }

  getCommentQuestionKey() {
    if (
      this.declarationConditions.isGlassMultiple(
        this.currentDossier?.claim?.glass
      )
    ) {
      return 'LastStep.Question1TextMultiple';
    }

    return 'LastStep.Question1Text';
  }

  displayInsurerLogo() {
    const insuranceChoiceId = this.insuranceSuggestion.find(
      (value) => value.insuranceName === this.form?.get('insuranceName')?.value
    )?.id;
    return !!insuranceChoiceId;
  }

  displayCheckboxRow() {
    return (
      this.featureFlippingService.isFeatureActivated(
        'b2c-field-required-gpdr'
      ) ||
      this.featureFlippingService.isFeatureActivated(
        'b2c-field-display-marketing-data'
      )
    );
  }

  onInsuranceChoiceChange(event: MatRadioChange): void {
    if (event.value !== 'true') {
      this.form?.get('insuranceName').setValue(null);
    }
    if (
      this.featureFlippingService.isFeatureActivated(
        'b2c-field-required-insurance-choice'
      )
    ) {
      if (event.value === 'true') {
        this.form?.get('insuranceName')?.setValidators([Validators.required]);
        this.form?.get('insuranceName')?.updateValueAndValidity();
        this.onInputChange({
          target: { value: this.form?.get('insuranceName')?.value },
        } as any);
      } else {
        this.form?.get('insuranceName')?.clearValidators();
        this.form?.get('insuranceName')?.updateValueAndValidity();
      }
    }
  }

  onOwnerShipTypeChange(event): void {
    if (event.value !== 'businesslike') {
      this.form?.get('ownerShipName')?.setValue(null);
    }
    if (
      this.featureFlippingService.isFeatureActivated(
        'b2c-field-required-ownership'
      )
    ) {
      if (event.value === 'businesslike') {
        this.form?.get('ownerShipName')?.setValidators([Validators.required]);
        this.form?.get('ownerShipName')?.updateValueAndValidity();
      } else {
        this.form?.get('ownerShipName')?.clearValidators();
        this.form?.get('ownerShipName')?.updateValueAndValidity();
      }
    }
  }

  onSelectSuggestion(event) {
    this.form?.get('insuranceName')?.setValue(event);
    this.errorImage = false;
    this.changeDetectorRef.detectChanges();
  }

  onInputChange(event: Event) {
    const value = (event as HTMLElementEvent<HTMLInputElement>)?.target?.value;

    this.filteredInsuranceSuggestion =
      this.configurationService.filterInsuranceSuggestions(
        this.insuranceSuggestion,
        value
      );
    this.changeDetectorRef.detectChanges();
  }

  onInputFocusOut() {
    if (
      !this.featureFlippingService.isFeatureActivated(
        'b2c-insurance-custom-name'
      )
    )
      return;

    const inputValue = this.form
      ?.get('insuranceName')
      ?.value?.toLowerCase()
      .trim();
    // Compare partial inputValue to suggestions
    const matchedSuggestion = this.filteredInsuranceSuggestion.find(
      (s) => s.toLowerCase().trim() === inputValue
    );
    if (matchedSuggestion) {
      this.form?.get('insuranceName')?.setValue(matchedSuggestion);
      return;
    }
    if (
      this.customInsuranceNameEnabled ||
      (!this.customInsuranceNameEnabled && this.clickingCheckbox)
    )
      return;

    this.form?.get('insuranceName')?.setValue('');
  }

  onInputFocusIn() {
    this.onInputChange({
      target: { value: this.form?.get('insuranceName')?.value },
    } as any);
  }

  onCustomInsuranceCheckboxChange($event) {
    if (!$event) {
      this.form?.get('insuranceName')?.setValue('');
    }

    this.customInsuranceNameEnabled = $event;
    this.changeDetectorRef.detectChanges();
  }

  onCheckDown() {
    this.clickingCheckbox = true;
  }

  onCheckUp() {
    setTimeout(() => (this.clickingCheckbox = false), 0);
  }

  bookAppointment(): void {
    const dossier: DossierLastStepForm = this.form?.getRawValue();
    dossier.contactPeriod = this.translator.getInstantTranslation(
      'GlobalApplicativeConstants.contactPeriodTimes.globalInfo',
      { period: dossier?.contactPeriod }
    );
    dossier.customerLocation = this.modifiedLocation;
    this.bookClickEvent.emit(dossier);
  }

  onChangePostalCode($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.postalCode = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
    }
  }

  onChangeCity($event: Event) {
    if (this.modifiedLocation) {
      this.modifiedLocation.addressElements.city = (
        $event as HTMLElementEvent<HTMLInputElement>
      ).target.value;
    }
  }

  onUpdateAddressSuggestions($event: string) {
    if ($event !== this.selectedAddressStr) {
      this.addressSuggestionsSubject$.next($event);
    }
  }

  ngOnDestroy(): void {
    this.addressSuggestionsSubject$.complete();
  }

  onSelectAddress(locationData: LocationData) {
    if (locationData.placeId) {
      this.modifiedLocation = null;
      this.showUnknownAddressForm = false;
      this.updateFormValidators();
      this.sandboxService
        .getGeoCodeFromPlaceId(locationData.placeId)
        .subscribe({
          next: (location) => {
            this.selectedAddressStr = location.address;
            this.modifiedLocation = location;

            if (
              !location?.addressElements?.city ||
              !location?.addressElements?.postalCode
            ) {
              this.sandboxService
                .getPostalCodeFromGeoCode(location?.geoCoordinates)
                .subscribe((result) => {
                  this.selectedAddressStr = result.address;
                  this.modifiedLocation = result;
                  this.showUnknownAddressForm =
                    !result?.addressElements?.city ||
                    !result?.addressElements?.postalCode;

                  this.fetchFittersFromGeoCode(
                    result?.geoCoordinates?.lat,
                    result?.geoCoordinates?.lon,
                    15
                  );

                  this.form
                    .get('customerLocation')
                    ?.setValue(this.modifiedLocation?.address);
                });
            } else {
              this.fetchFittersFromGeoCode(
                location?.geoCoordinates?.lat,
                location?.geoCoordinates?.lon,
                15
              );

              this.form
                .get('customerLocation')
                ?.setValue(this.modifiedLocation?.address);
            }

            this.changeDetectorRef.detectChanges();
            this.changeFocusOnUnknownAddress();
          },
          error: (error) => {
            console.error(error);
          },
        });
    } else {
      this.modifiedLocation = {
        placeId: null,
        locationProvider: 'NONE',
        isKnownAddress: false,
        isCompleteAddress: false,
        addressElements: {
          country: 'fr',
        },
        geoCoordinates: null,
        address: locationData?.address || '',
      };
      this.showUnknownAddressForm = true;
      this.form
        .get('customerLocation')
        ?.setValue(this.modifiedLocation?.address);
      this.updateFormValidators();
      this.changeDetectorRef.detectChanges();
      this.changeFocusOnUnknownAddress();
    }
  }

  fetchFittersFromGeoCode(lat: number, lon: number, distance: number) {
    this.fittersSB
      .fetchFitters({
        distance: distance,
        lat: lat,
        lon: lon,
        shouldWidenSearch: true,
        onlyAgendaFitters: true,
      })
      ?.subscribe({
        next: (result) => {
          this.fitterSelectList =
            result?.fitters
              ?.sort(
                (fitter1, fitter2) =>
                  (fitter1.distance || 9999) - (fitter2.distance || 9999)
              )
              ?.map((fitter) => {
                return {
                  fitterId: fitter?.id,
                  ihm: fitter?.brand + ' - ' + fitter?.city,
                  brandId: fitter?.brandId,
                  timezone: fitter?.timezone,
                };
              }) ?? [];

          this.fittersByAddress = result?.fitters;
          const selectedFitter = {
            fitterId: null,
            ihm: this.translator.getInstantTranslation(
              this.fitterSelectList?.length
                ? 'VerifyCustomerDataBottomSheet.noFitterChoice'
                : 'VerifyCustomerDataBottomSheet.noFitterFound'
            ),
            brandId: null,
            timezone: null,
          };
          this.fitterSelectList.unshift(selectedFitter);
          this.onFitterSelect(selectedFitter);
          this.changeDetectorRef.detectChanges();
        },
        error: () => (this.fitterSelectList = []),
      });
  }

  changeFocusOnUnknownAddress() {
    if (!this.showUnknownAddressForm) {
      return;
    }
    if (this.modifiedLocation?.addressElements?.city) {
      this.focusManager.focusFirstElementAllowed(
        'sc-dossier-last-step-form form .customer-infos-container .customer-location-container .address-bloc #postalCode'
      );
    } else {
      this.focusManager.focusFirstElementAllowed(
        'sc-dossier-last-step-form form .customer-infos-container .customer-location-container .address-bloc #city'
      );
    }
  }

  onFitterSelect($event) {
    this.form.get('selectedFitter')?.setValue($event);
  }
}
